import { useMutation } from '@tanstack/react-query';

import { ApiError, unwrap } from '@/api/envelope';
import { transitionDocument } from '@/api/generated';
import { useToast } from '@/components/ui/useToast';

import type { TransitionTarget } from './documentActions';
import { toDocumentRow, type DocumentRow } from './documentLabels';
import { useInvalidateDocuments } from './useDocuments';

export type { TransitionTarget } from './documentActions';

/** What the toast says once a move is made. */
function moved(row: DocumentRow, to: TransitionTarget): string {
  const title = `“${row.title}”`;
  if (to === 'ARCHIVED') return `${title} was removed.`;
  if (to === 'INACTIVE') return `${title} is unpublished.`;
  return row.lifecycle === 'INACTIVE'
    ? `${title} is published again.`
    : `${title} is published.`;
}

/**
 * Publish, Unpublish, Republish and Remove (P11, P13, PB8), shared by the row
 * menu and the drawer: one request to the transition route, the outcome
 * toasted, and the library asked for again either way (P14) - on success
 * because the row changed, on a refusal because the row on screen was wrong.
 * A 409 and a 403 are told in the backend's words; nothing is predicted.
 */
export function useDocumentTransition() {
  const invalidate = useInvalidateDocuments();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ row, to }: { row: DocumentRow; to: TransitionTarget }) => {
      const reply = await transitionDocument({
        path: { document_id: row.id },
        body: { to },
        throwOnError: true,
      });
      return toDocumentRow(unwrap(reply.data));
    },
    onSuccess: (_row, { row, to }) => {
      toast.show({ message: moved(row, to) });
    },
    onError: (error) => {
      toast.show({
        message:
          (error instanceof ApiError ? error.serverMessage : null) ??
          'That change could not be made. Please try again.',
        severity: 'error',
      });
    },
    onSettled: invalidate,
  });
}
